// Canonical origin used in sitemap.xml, canonical links and Open Graph tags.
export const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://hetheraengineering.com').replace(
  /\/+$/,
  '',
);

// Business identity shown on the website. Meta's WhatsApp Business
// verification compares these against the CAC documents, so keep the legal
// name, RC number and registered address exactly as CAC records them.
export const COMPANY = {
  legalName: 'Hethera Engineering Ltd',
  brandName: 'Hethera',
  rcNumber: '9859449',
  incorporatedOn: '15 September 2026',
  companyType: 'Private company limited by shares',
  registeredAddress: '16, Adisa Basua Street, Surulere, Lagos State, Nigeria',
  address: {
    street: '16, Adisa Basua Street',
    locality: 'Surulere',
    region: 'Lagos State',
    country: 'NG',
  },
  supportEmail: 'info@hetheraengineering.com',
  phone: '+2348119995541',
  phoneDisplay: '+234 811 999 5541',
  supportHours: 'Monday to Friday, 9:00–18:00 WAT',
  policyEffectiveDate: '3 November 2026',
} as const;

// The WhatsApp Business number customers chat with, international format,
// digits only. REACT_APP_WHATSAPP_NUMBER overrides it at build time.
const whatsappDigits = (process.env.REACT_APP_WHATSAPP_NUMBER || '2347044529110').replace(
  /\D/g,
  '',
);

// The WhatsApp Business short link every "Chat on WhatsApp" button, the QR
// code (public/whatsapp-qr.svg, made by scripts/whatsapp-qr.js) and the
// payment-success page's redirect open. If it changes, re-run that script.
const WHATSAPP_CHAT_LINK = 'https://wa.me/message/DEUJTURPSIP3B1';

export const WHATSAPP = {
  digits: whatsappDigits,
  href: WHATSAPP_CHAT_LINK,
  display: whatsappDigits ? formatNigerianNumber(whatsappDigits) : 'Coming soon',
  qr: '/whatsapp-qr.svg',
};

// The Hethera API origin, for the payment-success page's card check.
// REACT_APP_API_URL is set per environment in .github/workflows.
export const API_URL = (process.env.REACT_APP_API_URL || 'https://api.hetheraengineering.com').replace(
  /\/+$/,
  '',
);

// Hours of inactivity before the assistant forgets a conversation; mirrors
// AGENT_IDLE_TIMEOUT_MINUTES (240) in the Hethera backend.
export const CHAT_MEMORY_HOURS = 4;

// Spending reports — mirror the Hethera backend (REPORT_LINK_TTL_HOURS,
// REPORT_RETENTION_DAYS) and the report page's own address.
export const REPORT = {
  url: 'https://report.hetheraengineering.com',
  host: 'report.hetheraengineering.com',
  linkHours: 24,
  sessionMinutes: 30,
  retentionDays: 30,
} as const;

// Pricing — mirrors the backend's defaults (../Hethera/docs/pricing-acceptance-
// criteria.md, app/service/pricing.py). An admin can change them on the
// dashboard; update these when they do.
// Cable: a service fee on top of the plan (SERVICE_FEE_CABLE).
export const CABLE_SERVICE_FEE_NAIRA = 110;
// Airtime: the most per purchase (AIRTIME_MAX_PER_PURCHASE). Data has no cap.
export const AIRTIME_MAX_NAIRA = 2000;
// Electricity: what you enter is what your card pays; the card processing fee
// (Paystack's fee, PAYSTACK_FEE_*) comes out of it and the rest is units.
export const ELECTRICITY_FEE = { pct: 1.5, flat: 100, flatFrom: 2500, cap: 2000 } as const;
// Electricity by bank transfer into the customer's own account: Paystack's
// dedicated-account fee comes out instead (PAYSTACK_TRANSFER_FEE_*). `live`
// mirrors the backend's TRANSFER_PAYMENTS_ENABLED: keep it false until that
// is on in production, so the site never describes something people can't use.
export const BANK_TRANSFER = { live: false, pct: 1, cap: 300, minimum: 5000 } as const;

// Ejor requests stay payable this long (backend EJOR_EXPIRY_HOURS).
export const EJOR_EXPIRY_HOURS = 48;

// Dash gifts — mirror the backend's GIFT_* settings
// (../Hethera/docs/gift-acceptance-criteria.md).
export const DASH = {
  step: 500, // GIFT_AMOUNT_STEP
  airtimeMin: 500, // GIFT_AIRTIME_MIN
  airtimeMax: 2_000, // the airtime cap applies to gifts too (AIRTIME_MAX_PER_PURCHASE)
  // GIFT_ELECTRICITY_MIN_FLOOR is ₦5,000 of electricity; the card processing
  // fee comes out of what the sender pays, so the least they pay is ₦5,500.
  electricityMin: 5_500,
  electricityMax: 50_000, // GIFT_ELECTRICITY_MAX
  maxDaysAhead: 60, // GIFT_MAX_SCHEDULE_DAYS
  responseDays: 30, // GIFT_RESPONSE_DAYS
  slots: '8 am, 12 noon or 6 pm', // GIFT_DELIVERY_SLOTS, Lagos time
} as const;

// Scheduled payments: airtime and data payments under this get no
// day-before reminder (backend SCHEDULE_SMALL_RUN_AMOUNT).
export const SCHEDULE_SMALL_RUN_NAIRA = 2_000;

function formatNigerianNumber(digits: string): string {
  const m = digits.match(/^234(\d{3})(\d{3})(\d{4})$/);
  return m ? `+234 ${m[1]} ${m[2]} ${m[3]}` : `+${digits}`;
}
